import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { appConfig } from '../config/appConfig';

const API_URL = process.env.REACT_APP_API_URL;

const BLOCKING_ERRORS = new Set(['not_authorized', 'deactivated', 'missing_pgp_perm']);

function decodeJwt(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
    );
  } catch (e) {
    return null;
  }
}

export default function LoginPage() {
  const [error, setError] = useState('');
  const [blockedReason, setBlockedReason] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  const logoSrc = require(`../assets/${appConfig.logo || 'encore.jpg'}`);

  // Handle token / error returned from Microsoft callback
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get('token');
    const errFromUrl = params.get('error');
    const emailFromUrl = params.get('email');
    if (tokenFromUrl) {
      // On the PGP frontend, only users with the `pgp` perm (or admins) are allowed in.
      if (appConfig.isPgp) {
        const claims = decodeJwt(tokenFromUrl);
        const okay =
          !!claims &&
          (claims.is_admin || (Array.isArray(claims.perms) && claims.perms.includes('pgp')));
        if (!okay) {
          setBlockedReason({ code: 'missing_pgp_perm', email: claims?.email });
          window.history.replaceState({}, '', '/login');
          return;
        }
      }
      login(tokenFromUrl);
      navigate('/', { replace: true });
      return;
    }
    if (errFromUrl) {
      if (BLOCKING_ERRORS.has(errFromUrl)) {
        setBlockedReason({ code: errFromUrl, email: emailFromUrl });
      } else {
        const messages = {
          invalid_state: 'Sign-in expired. Please try again.',
          no_email_claim: 'Could not read your email from Microsoft.',
          token_exchange_failed: 'Microsoft sign-in failed. Please try again.',
        };
        setError(messages[errFromUrl] || `Sign-in failed (${errFromUrl})`);
      }
      window.history.replaceState({}, '', '/login');
    }
  }, [location.search, login, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleMicrosoftLogin = () => {
    const frontend = appConfig.isPgp ? 'pgp' : 'encore';
    window.location.href = `${API_URL}/auth/microsoft/login?frontend=${frontend}`;
  };

  if (blockedReason) {
    return (
      <BlockedView
        reason={blockedReason}
        logoSrc={logoSrc}
        onTryAgain={() => setBlockedReason(null)}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img
            src={logoSrc}
            alt={`${appConfig.appName} Logo`}
            className="h-24 w-auto mx-auto mb-4"
          />
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            {appConfig.appName} Analytics
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to continue
          </p>
        </div>

        <div>
          <button
            type="button"
            onClick={handleMicrosoftLogin}
            className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 rounded-md bg-white text-gray-800 hover:bg-gray-50"
          >
            <MicrosoftLogo />
            <span>Sign in with Microsoft</span>
          </button>
        </div>

        {error && (
          <div className="text-red-600 text-sm text-center">{error}</div>
        )}
      </div>
    </div>
  );
}

function BlockedView({ reason, logoSrc, onTryAgain }) {
  const titles = {
    deactivated: 'Account deactivated',
    not_authorized: 'Access not granted',
    missing_pgp_perm: 'PGP access not granted',
  };
  const bodies = {
    deactivated:
      'Your account has been deactivated. Contact an admin if you think this is a mistake.',
    not_authorized:
      'Your Microsoft account is not on the allowlist for this app. Ask an admin to invite you.',
    missing_pgp_perm:
      'Your account does not have PGP access. Ask an admin to grant you the PGP permission.',
  };
  const title = titles[reason.code] || 'Sign in failed';
  const body = bodies[reason.code] || 'An error occurred during sign-in.';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <img
            src={logoSrc}
            alt={`${appConfig.appName} Logo`}
            className="h-24 w-auto mx-auto mb-4"
          />
        </div>
        <div className="bg-white border border-red-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              {reason.email && (
                <p className="text-sm text-gray-500 mt-1">
                  Signed in as <span className="font-medium text-gray-700">{reason.email}</span>
                </p>
              )}
              <p className="text-sm text-gray-700 mt-3">{body}</p>
            </div>
          </div>
        </div>
        <div className="text-center">
          <button
            onClick={onTryAgain}
            className="text-sm text-blue-600 hover:underline"
          >
            Back to sign in
          </button>
        </div>
      </div>
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}
